import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Panel, STATUS, fmtDate } from "@/components/rc/shared";
import { EhcpForm } from "@/components/rc/EhcpForm";

const FILTERS = [{ id: "all", label: "All" }, ...Object.entries(STATUS).map(([id, s]) => ({ id, label: s.label }))];

const Row = ({ r, canEdit, canDelete, onEdit, onDelete }) => (
  <li className="grid gap-3 rounded-2xl border-2 border-line bg-paper p-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`ehcp-row-${r.pupil_ref}`}>
    <div>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-display text-xl font-semibold">{r.pupil_ref}</span>
        <span className="flex items-center gap-1.5 rounded-full border-2 border-line px-2.5 py-0.5 text-sm font-bold" data-testid={`ehcp-status-${r.pupil_ref}`}>
          <span className={`dot dot-${STATUS[r.status]?.tone}`} aria-hidden="true" />{STATUS[r.status]?.label}
        </span>
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        Review due <strong className="text-ink">{fmtDate(r.review_due)}</strong> · {r.stage}{r.year_group ? ` · ${r.year_group}` : ""}{r.primary_need ? ` · ${r.primary_need}` : ""}{r.lead ? ` · Lead: ${r.lead}` : ""}
      </p>
      {r.notes && <p className="mt-2 whitespace-pre-wrap text-sm">{r.notes}</p>}
    </div>
    {(canEdit || canDelete) && (
      <div className="flex gap-2">
        {canEdit && <button type="button" onClick={() => onEdit(r)} className="btn btn-ghost btn-sm" aria-label={`Edit ${r.pupil_ref}`} data-testid={`ehcp-edit-${r.pupil_ref}`}><Pencil size={14} aria-hidden /> Edit</button>}
        {canDelete && <button type="button" onClick={() => onDelete(r)} className="btn btn-ghost btn-sm" aria-label={`Delete ${r.pupil_ref}`} data-testid={`ehcp-delete-${r.pupil_ref}`}><Trash2 size={14} aria-hidden /></button>}
      </div>
    )}
  </li>
);

export const EhcpTracker = ({ records, canEdit, canDelete, reload }) => {
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const shown = filter === "all" ? records : records.filter((r) => r.status === filter);
  const remove = async (r) => {
    if (!window.confirm(`Delete the review record for ${r.pupil_ref}?`)) return;
    try {
      await api.delete(`/rc/ehcp/${r.id}`);
      toast.success("Record deleted");
      reload();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Panel id="ws-ehcp" eyebrow="Pupil reference codes only" title="EHCP annual review tracker"
      action={canEdit && <button type="button" onClick={() => setEditing({})} className="btn btn-brand" data-testid="ehcp-add-button"><Plus size={16} aria-hidden /> Add review</button>}>
      <div role="group" aria-label="Filter reviews" className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map((x) => {
          const n = x.id === "all" ? records.length : records.filter((r) => r.status === x.id).length;
          return <button key={x.id} type="button" aria-pressed={filter === x.id} onClick={() => setFilter(x.id)} className={`chip ${filter === x.id ? "bg-brand text-brand-fg" : ""}`} data-testid={`ehcp-filter-${x.id}`}>{x.label} ({n})</button>;
        })}
      </div>
      {shown.length ? (
        <ul className="space-y-3" data-testid="ehcp-list">{shown.map((r) => <Row key={r.id} r={r} canEdit={canEdit} canDelete={canDelete} onEdit={setEditing} onDelete={remove} />)}</ul>
      ) : (
        <p className="rounded-2xl border-2 border-dashed border-line p-6 text-ink-soft" data-testid="ehcp-empty">{records.length ? "No reviews match this filter." : "No reviews yet. Add your first one using a pupil reference code."}</p>
      )}
      {editing && <EhcpForm key={editing.id || "new"} record={editing.id ? editing : null} open onOpenChange={(o) => !o && setEditing(null)} onSaved={reload} />}
    </Panel>
  );
};
